import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import {
  Sheet,
  SheetContent,
  SheetTitle,
} from "@/components/ui/sheet";
import CustomSelect from "../../CustomSelect";
import { useContext, useState, useEffect, useCallback, ChangeEvent } from "react";
import { UserPlus, Search } from "lucide-react";
import TableContainer, { Column } from "../../TableContainer";
import { appointment, employee } from "../../../types";
import { trpc } from "../../../trpc";
import MerchantContext from "../../../contexts/MerchantContext";
import UserContext from "../../../contexts/UserContext";
import { isDataScopedEmployee } from "@/utils/permissions";
import { usePipeline } from "../../../contexts/PipelineContext";
import { date } from "../../../../../agent/src/common/time";
import { useSearchParams } from "react-router-dom";
import { APPOINTMENT_COLUMNS } from "../../../utils/columns";
import {
  getBadgeColorByPipelinePosition,
  getBadgeBgColorByPipelinePosition,
  getSystemPositionDescription,
} from "../../../utils/index";
import AppointmentDetailPanel from "./AppointmentDetailPanel";

const ITEMS_PER_PAGE = 50;

const AppointmentsContent = () => {
  const [employeeId, setEmployeeId] = useState<string | undefined>(undefined);
  const [employeesData, setEmployeesData] = useState<employee[]>([]);
  const [leadPhone, setLeadPhone] = useState<string | undefined>(undefined);
  const [fetching, setFetching] = useState(true);
  const [appointmentsData, setAppointmentsData] = useState<appointment[]>([]);
  const { merchantId } = useContext(MerchantContext);
  const { roles, employees: userEmployees, employeeIds: userEmployeeIds } = useContext(UserContext);
  const isBasicEmployee = isDataScopedEmployee(roles);
  const showOwnEmployeeSelect = isBasicEmployee && userEmployees.length >= 2;
  const [ownEmployeeId, setOwnEmployeeId] = useState<string | undefined>(undefined);
  const selectedOwnEmployeeId =
    ownEmployeeId && userEmployeeIds.includes(ownEmployeeId) ? ownEmployeeId : userEmployeeIds[0];
  const queryEmployeeId = showOwnEmployeeSelect ? selectedOwnEmployeeId : employeeId;
  const { allPositions } = usePipeline();

  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedAppointmentId, setSelectedAppointmentId] = useState<string | undefined>(undefined);

  // Cursor-based navigation
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [prevCursors, setPrevCursors] = useState<string[]>([]);

  // Handle URL query param on mount and when it changes
  const appointmentIdFromUrl = searchParams.get("id");

  useEffect(() => {
    if (appointmentIdFromUrl) {
      setSelectedAppointmentId(appointmentIdFromUrl);
    }
  }, [appointmentIdFromUrl]);

  const handleEmployeeSelect = (selectedLabel: string | undefined) => {
    if (!selectedLabel) {
      setEmployeeId(undefined);
      return;
    }
    const backendValue = employeesData.find((employee) => employee.name === selectedLabel);

    setEmployeeId(backendValue?.id);

    // Reset navigation when filters change
    setCursor(undefined);
    setPrevCursors([]);
  };

  const handleOwnEmployeeSelect = (selectedLabel: string | undefined) => {
    const selected = userEmployees.find((employee) => employee.name === selectedLabel);
    if (!selected) return;
    setOwnEmployeeId(selected.id);
    setCursor(undefined);
    setPrevCursors([]);
  };

  const handleLeadPhoneSelect = (event: ChangeEvent<HTMLInputElement>) => {
    const numericValue = event.target.value.replace(/\D/g, "");
    setLeadPhone(numericValue);

    // Reset navigation when filters change
    setCursor(undefined);
    setPrevCursors([]);
  };

  const employees = trpc.employees.list.useQuery(
    { merchantId },
    { enabled: !!merchantId, onSuccess: (data) => setEmployeesData(data) }
  );

  const appointments = trpc.appointments.list.useQuery(
    {
      merchantId,
      employeeId: queryEmployeeId,
      leadPhone,
      limit: ITEMS_PER_PAGE,
      cursor,
    },
    {
      enabled: !!merchantId,
      onSuccess: (data) => {
        setAppointmentsData(data.data);
        setFetching(false);
      },
      onError: () => {
        setFetching(false);
      },
    }
  );

  // Fetch selected appointment details
  const { data: appointmentDetail, isLoading: isLoadingDetail } = trpc.appointments.get.useQuery(
    {
      merchantId,
      id: selectedAppointmentId || "",
    },
    {
      enabled: !!merchantId && !!selectedAppointmentId,
    }
  );

  // Fetch lead data for the selected appointment
  const { data: leadData } = trpc.leads.get.useQuery(
    { merchantId, id: appointmentDetail?.leadId || "" },
    { enabled: !!merchantId && !!appointmentDetail?.leadId }
  );

  const refreshAppointments = async () => {
    setCursor(undefined);
    setPrevCursors([]);
    await appointments.refetch();
    await employees.refetch();
  };

  const handleAppointmentSelect = (id: string) => {
    setSelectedAppointmentId(id);
    setSearchParams({ id });
  };

  const handleDrawerClose = () => {
    setSelectedAppointmentId(undefined);
    setSearchParams({});
  };

  const isDrawerOpen = !!selectedAppointmentId;

  useEffect(() => {
    refreshAppointments();
  }, [employeeId]);

  const handleNext = useCallback(() => {
    if (appointments.data && appointments.data.cursor) {
      // Store current cursor in prevCursors before changing page
      setPrevCursors((prev) => [...prev, cursor || ""]);

      // Use the cursor from the API response
      setCursor(appointments.data.cursor);
    }
  }, [cursor, appointments.data]);

  const handlePrevious = useCallback(() => {
    if (prevCursors.length > 0) {
      // Get the previous cursor
      const newPrevCursors = [...prevCursors];
      const prevCursor = newPrevCursors.pop();

      // Update state
      setPrevCursors(newPrevCursors);
      setCursor(prevCursor);
    }
  }, [prevCursors]);

  const renderCell = (appointment: appointment, columnKey: string) => {
    switch (columnKey) {
      case "createdAt":
        return (
          <p
            className="cursor-pointer hover:underline"
            onClick={() => handleAppointmentSelect(appointment.id)}
          >
            {date(appointment.createdAt).format("DD/MM/YYYY HH:mm")}
          </p>
        );
      case "employeeName":
        return <p>{appointment.employeeName ?? ""}</p>;
      case "leadName":
        return (
          <p
            className="cursor-pointer hover:underline"
            onClick={() => handleAppointmentSelect(appointment.id)}
          >
            {appointment.leadName}
          </p>
        );
      case "leadPhone":
        return <p>{appointment.leadPhone}</p>;
      case "listingCode":
        return (
          <p>
            {appointment.listingCode !== "NONE" ? appointment.listingCode : appointment.projectCode}
          </p>
        );
      case "projectCode":
        return <p>{appointment.projectCode}</p>;
      case "date":
        return <p>{appointment.date}</p>;
      case "time":
        return <p>{appointment.time}</p>;
      case "pipelinePosition":
        const pipelinePosition = allPositions.find(
          (option) => option.id === appointment.pipelinePosition
        );
        const tooltipContent = pipelinePosition?.isSystem
          ? getSystemPositionDescription(appointment.pipelinePosition || "")
          : "Subestado personalizado creado por el comercio";

        return (
          <div className="relative">
            <p
              className="group relative inline-block w-full rounded-full p-[4px_8px] text-center"
              style={{
                color: getBadgeColorByPipelinePosition(
                  appointment.pipelinePosition || "",
                  allPositions
                ),
                backgroundColor: getBadgeBgColorByPipelinePosition(
                  appointment.pipelinePosition || "",
                  allPositions
                ),
              }}
              title={tooltipContent}
            >
              {pipelinePosition?.displayName || appointment.pipelinePosition}
            </p>
          </div>
        );
      case "action1":
        return (
          <div className="flex justify-center rounded-[100px] bg-palomma-primary p-[4px_8px]">
            <p
              className="cursor-pointer text-white"
              onClick={() => handleAppointmentSelect(appointment.id)}
            >
              Ver detalles
            </p>
          </div>
        );
      default:
        return null;
    }
  };

  const renderMobileCard = (appointment: appointment) => {
    const pipelinePosition = allPositions.find(
      (option) => option.id === appointment.pipelinePosition
    );

    return (
      <div
        className="min-h-[44px] cursor-pointer p-3 hover:bg-gray-50"
        onClick={() => handleAppointmentSelect(appointment.id)}
      >
        <div className="mb-1 flex items-center justify-between">
          <p className="truncate text-sm font-medium">
            {appointment.leadName}
          </p>
          <p className="text-xs text-gray-500">
            {appointment.date}
          </p>
        </div>
        <div className="mb-1 flex items-center justify-between">
          <p className="truncate text-xs text-gray-600">
            {appointment.employeeName ?? "Sin asesor"}
          </p>
          <p className="text-xs text-gray-500">
            {appointment.time}
          </p>
        </div>
        <div className="flex items-center justify-between">
          <p className="text-xs text-gray-500">
            {appointment.leadPhone}
          </p>
          {pipelinePosition && (
            <span
              className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold"
              style={{
                color: getBadgeColorByPipelinePosition(appointment.pipelinePosition || "", allPositions),
                backgroundColor: getBadgeBgColorByPipelinePosition(appointment.pipelinePosition || "", allPositions),
              }}
            >
              {pipelinePosition.displayName || appointment.pipelinePosition}
            </span>
          )}
        </div>
      </div>
    );
  };

  if (!merchantId) {
    return (
      <div className="flex h-[calc(100dvh-60px)] items-center justify-center md:h-screen">
        <p className="text-gray-400">No hay comercio seleccionado</p>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col overflow-hidden bg-[#F9F9FB]">
      {/* Header with filters */}
      <div className="border-b border-gray-200 bg-white p-4">
        <p className="mb-3 text-lg font-bold">
          Citas tentativas
        </p>
        <div className="flex flex-wrap gap-4">
          {!isBasicEmployee && (
            <div className="w-full md:w-[30%]">
              <CustomSelect
                options={employeesData.map((employee) => employee.name)}
                selectedOption={
                  employeeId
                    ? employeesData.find((employee) => employee.id === employeeId)?.name
                    : undefined
                }
                onSelect={handleEmployeeSelect}
                placeholder="Selecciona un empleado"
                width="100%"
                icon={UserPlus}
              />
            </div>
          )}
          {showOwnEmployeeSelect && (
            <div className="w-full md:w-[30%]">
              <CustomSelect
                options={userEmployees.map((employee) => employee.name)}
                selectedOption={
                  userEmployees.find((employee) => employee.id === selectedOwnEmployeeId)?.name
                }
                onSelect={handleOwnEmployeeSelect}
                placeholder="Selecciona un asesor"
                width="100%"
                icon={UserPlus}
                deselectable={false}
              />
            </div>
          )}
          <div className="relative w-full md:w-[30%]">
            <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2">
              <Search className="size-4 text-gray-400" />
            </div>
            <Input
              value={leadPhone || ""}
              onChange={handleLeadPhoneSelect}
              placeholder="Buscar por teléfono..."
              className="pl-10"
            />
          </div>
        </div>
      </div>

      {/* Table content */}
      <div className="m-2 flex-1 overflow-y-auto rounded-md bg-white shadow-sm md:m-4">
        {fetching ? (
          <div className="flex h-[200px] items-center justify-center md:h-[300px]">
            <Spinner size="xl" />
          </div>
        ) : (
          <TableContainer
            columns={APPOINTMENT_COLUMNS as Column[]}
            data={appointmentsData}
            renderCell={renderCell}
            renderMobileCard={renderMobileCard}
            refetch={refreshAppointments}
            canPrevious={prevCursors.length > 0}
            canNext={!!appointments.data?.cursor}
            onPrevious={handlePrevious}
            onNext={handleNext}
            pagination={true}
          />
        )}
      </div>

      {/* Slide-over drawer for appointment details and conversation */}
      <Sheet open={isDrawerOpen} onOpenChange={(open) => !open && handleDrawerClose()}>
        <SheetContent
          side="right"
          className="w-full p-0 sm:max-w-none md:w-[80%] lg:w-[70%]"
        >
          <SheetTitle className="sr-only">Detalle de cita</SheetTitle>
          {isDrawerOpen && (
            <AppointmentDetailPanel
              appointment={appointmentDetail}
              leadData={leadData}
              isLoading={isLoadingDetail}
              merchantId={merchantId}
            />
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default AppointmentsContent;
